import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  SAMPLE_INVITEES,
  clearSampleSession,
  formatSampleCountdown,
  isSampleInviteLink,
  readSampleSession,
  startSampleSession,
} from "./sampleAccess";

const SLOT_ID = "venaqqi-sample-access-slot";

const sampleButton = {
  width: "100%",
  borderRadius: 28,
  border: "1px solid #e53935",
  background: "#171717",
  color: "#fff",
  padding: "14px 16px",
  fontSize: 16,
  fontWeight: 800,
  cursor: "pointer",
  letterSpacing: ".02em",
};

const findWelcomeLoginButton = () => {
  const buttons = Array.from(document.querySelectorAll("button"));
  return buttons.find(button => {
    if (String(button.textContent || "").trim() !== "Iniciar sesión") return false;
    const hostText = String(button.parentElement?.textContent || "");
    return hostText.includes("¡Bienvenido!") && hostText.includes("Crear cuenta") && hostText.includes("Hoy como invitado");
  }) || null;
};

const ensureSampleSlot = () => {
  const connected = document.getElementById(SLOT_ID);
  if (connected?.isConnected) return connected;
  const loginButton = findWelcomeLoginButton();
  if (!loginButton) return null;
  const slot = document.createElement("div");
  slot.id = SLOT_ID;
  slot.style.width = "100%";
  slot.style.margin = "10px 0 0";
  loginButton.insertAdjacentElement("afterend", slot);
  return slot;
};

const removeSampleSlot = () => {
  const slot = document.getElementById(SLOT_ID);
  if (slot) slot.remove();
};

const hideGuestBanner = () => {
  const candidates = Array.from(document.querySelectorAll('[role="status"]'));
  candidates.forEach(element => {
    const text = String(element.textContent || "");
    if (!text.includes("Modo invitado") || !text.includes("Exploras sin una cuenta registrada.")) return;
    if (!element.dataset.sampleOriginalDisplay) element.dataset.sampleOriginalDisplay = element.style.display || "__empty__";
    element.dataset.sampleHidden = "true";
    element.style.setProperty("display", "none", "important");
  });
};

const restoreGuestBanner = () => {
  document.querySelectorAll('[data-sample-hidden="true"]').forEach(element => {
    const original = element.dataset.sampleOriginalDisplay;
    element.style.removeProperty("display");
    if (original && original !== "__empty__") element.style.display = original;
    delete element.dataset.sampleOriginalDisplay;
    delete element.dataset.sampleHidden;
  });
};

export default function SampleAccessLayer({ reloadPage = () => window.location.reload() }) {
  const [now, setNow] = useState(Date.now());
  const [sampleSession, setSampleSession] = useState(() => readSampleSession());
  const [slot, setSlot] = useState(null);
  const [error, setError] = useState("");
  const linkAuthorized = useMemo(() => isSampleInviteLink(), []);
  const countdown = useMemo(
    () => formatSampleCountdown((sampleSession?.expiresAt || now) - now),
    [now, sampleSession],
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!sampleSession || now < sampleSession.expiresAt) return;
    clearSampleSession();
    setSampleSession(null);
    window.location.replace("/");
  }, [now, sampleSession]);

  useEffect(() => {
    if (sampleSession || !linkAuthorized) {
      removeSampleSlot();
      setSlot(null);
      return undefined;
    }

    const sync = () => {
      const next = ensureSampleSlot();
      if (next) setSlot(current => current === next ? current : next);
      else setSlot(null);
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      removeSampleSlot();
    };
  }, [sampleSession, linkAuthorized]);

  useEffect(() => {
    if (!sampleSession) {
      restoreGuestBanner();
      return undefined;
    }
    hideGuestBanner();
    const observer = new MutationObserver(hideGuestBanner);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      restoreGuestBanner();
    };
  }, [sampleSession]);

  const startFernandaAccess = () => {
    setError("");
    try {
      const started = startSampleSession(SAMPLE_INVITEES[0].id);
      setSampleSession(started);
      reloadPage();
    } catch (e) {
      setError(e?.message || "No se pudo iniciar el acceso temporal.");
    }
  };

  const sampleEntry = slot && linkAuthorized && !sampleSession
    ? createPortal(
      <div>
        <button
          type="button"
          style={sampleButton}
          onClick={startFernandaAccess}
          aria-label="Entrar con acceso temporal de 3 horas"
        >
          Acceso Fernanda · 3 horas
        </button>
        {error && <div role="alert" style={{ marginTop: 8, color: "#ffc9cc", fontSize: 12 }}>{error}</div>}
      </div>,
      slot,
    )
    : null;

  return <>
    {sampleEntry}

    {sampleSession && (
      <div
        data-sample-session-banner="true"
        aria-live="polite"
        style={{ position: "fixed", top: 10, left: 10, zIndex: 2147483000, maxWidth: "min(340px, calc(100vw - 20px))", background: "rgba(10,10,10,.94)", color: "#fff", border: "1px solid #e53935", borderRadius: 12, padding: "8px 10px", boxShadow: "0 8px 24px rgba(0,0,0,.35)", pointerEvents: "none" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <strong style={{ color: "#ef5350", fontSize: 11, letterSpacing: ".12em" }}>ACCESO TEMPORAL</strong>
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 12, fontWeight: 800 }}>{countdown}</span>
        </div>
        <div style={{ fontSize: 12, marginTop: 3, lineHeight: 1.35 }}>Acceso de entrevista: <strong>{sampleSession.inviteeName}</strong></div>
      </div>
    )}
  </>;
}
